import React, { useContext, useEffect, useMemo, useState } from "react";
import { AlertTitle, Box, Button, Divider, Paper, Typography } from "@mui/material";
import { Feature, LineString } from "geojson";
import { omit } from "lodash";

import { LoadingButton } from "ui";

import { selectLayerByVersionAndLayerKey, useGetFeaturesQuery } from "fond/api/layersSlice";
import { DRAWING_PROPERTIES_TO_EXCLUDE } from "fond/constants";
import MapOverlay from "fond/map/MapOverlay";
import { MapContext } from "fond/map/MapProvider";
import { EditMode, setEditMode } from "fond/project/redux";
import { addImport } from "fond/redux/imports";
import { featureCollection } from "fond/turf";
import { MuPowerEditableLayerIds, MuPowerInputLayerIds } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { BlockSpinner, ConfirmModal, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

import MuPowerLeadInHandler from "./MuPowerLeadInHandler";

import { Alert } from "../InputDataPanel.styles";

const MuPowerLeadInDraw: React.FC = () => {
  const dispatch = useAppDispatch();
  const { drawControl, isDirty: isDrawingDirty, map, setDrawMode } = useContext(MapContext);
  const { goBack } = useStackedNavigationContext();
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const versionId = useAppSelector((state) => state.project.versionId);

  const leadinsLayer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId: MuPowerEditableLayerIds.leadins }));
  const ugPointsLayer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId: MuPowerEditableLayerIds.ugPoints }));
  const addressesLayer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId: MuPowerInputLayerIds.addresses }));

  const { data: leadinFeatures, isLoading: leadinsLoading } = useGetFeaturesQuery({ layerId: leadinsLayer?.ID ?? "" }, { skip: !leadinsLayer?.ID });
  const { data: ugPointFeatures, isLoading: ugPointsLoading } = useGetFeaturesQuery(
    { layerId: ugPointsLayer?.ID ?? "" },
    { skip: !ugPointsLayer?.ID }
  );
  const { data: addressFeatures, isLoading: addressesLoading } = useGetFeaturesQuery(
    { layerId: addressesLayer?.ID ?? "" },
    { skip: !addressesLayer?.ID }
  );

  const isLoading = leadinsLoading || ugPointsLoading || addressesLoading;

  useEffect(() => {
    dispatch(setEditMode(EditMode.edit));
    return () => {
      dispatch(setEditMode(EditMode.none));
    };
  }, [dispatch]);

  // Hide the existing leadins vector tile layer while editing to avoid seeing features twice.
  useEffect(() => {
    if (!map) return undefined;
    const layerKey = MuPowerEditableLayerIds.leadins;
    const hiddenLayers: string[] = [];

    map.getStyle().layers?.forEach((layer) => {
      if ("source-layer" in layer && layer["source-layer"] === layerKey) {
        hiddenLayers.push(layer.id);
        map.setLayoutProperty(layer.id, "visibility", "none");
      }
    });

    return () => {
      hiddenLayers.forEach((id) => {
        if (map.getLayer(id)) {
          map.setLayoutProperty(id, "visibility", "visible");
        }
      });
    };
  }, [map]);

  const initialFeatures: Feature<LineString>[] = useMemo(() => {
    return (leadinFeatures ?? []).map((f) => ({
      ...f,
      properties: { ...f.properties, layerId: MuPowerEditableLayerIds.leadins },
    })) as Feature<LineString>[];
  }, [leadinFeatures]);

  const snapTargetFeatures: Feature[] = useMemo(() => {
    const ugPoints = (ugPointFeatures ?? []).map((f) => ({ ...f, properties: { ...f.properties, layerId: MuPowerEditableLayerIds.ugPoints } }));
    const addresses = (addressFeatures ?? []).map((f) => ({ ...f, properties: { ...f.properties, layerId: MuPowerInputLayerIds.addresses } }));
    return [...ugPoints, ...addresses] as Feature[];
  }, [ugPointFeatures, addressFeatures]);

  const handleBack = () => {
    if (isDrawingDirty) {
      setShowConfirm(true);
    } else {
      setDrawMode("no_feature");
      goBack();
    }
  };

  const handleConfirmDiscard = () => {
    setShowConfirm(false);
    setDrawMode("no_feature");
    goBack();
  };

  const handleFinish = async () => {
    if (!drawControl.current) return;
    setIsSaving(true);

    const allFeatures = drawControl.current
      .getAll()
      .features.filter((f) => f.properties?.layerId === MuPowerEditableLayerIds.leadins && f.geometry.coordinates.length >= 2)
      .map((f) => ({
        ...f,
        properties: omit(f.properties, DRAWING_PROPERTIES_TO_EXCLUDE),
      }));

    const layerKey = MuPowerEditableLayerIds.leadins;
    const fc = featureCollection(allFeatures);
    const file = new File([JSON.stringify(fc)], `${layerKey.split("/").pop()}.geojson`, { type: "application/geo+json" });

    await dispatch(
      addImport({
        versionId,
        layerKey,
        displayName: layerKey.split("/").pop() || layerKey,
        files: [file],
      })
    );

    setDrawMode("no_feature");
    setIsSaving(false);
    goBack();
  };

  if (isLoading) {
    return (
      <>
        <StackedNavigationHeader onBackButtonClick={handleBack} title="Edit Leadins" />
        <Box display="flex" justifyContent="center" p={4}>
          <BlockSpinner spinnerProps={{ size: 30 }} />
        </Box>
      </>
    );
  }

  return (
    <>
      <StackedNavigationHeader onBackButtonClick={handleBack} title="Edit Leadins" />
      <Box data-testid="mu-power-leadin-draw-panel" padding={1}>
        <Alert severity="info" data-testid="layer-alert">
          <AlertTitle sx={{ mb: 0 }}>Edit Lead-ins</AlertTitle>
        </Alert>
        <Divider sx={{ mt: 3 }} />
        <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
          <Button color="primary" size="small" onClick={handleBack} disabled={isSaving}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            size="small"
            onClick={handleFinish}
            sx={{ ml: 1, px: 2 }}
            data-testid="finish-button"
            disabled={!isDrawingDirty}
            loading={isSaving}
          >
            Finish
          </LoadingButton>
        </Box>
      </Box>

      <MuPowerLeadInHandler initialFeatures={initialFeatures} snapTargetFeatures={snapTargetFeatures} />

      <ConfirmModal
        open={showConfirm}
        content="Are you sure you want to discard your changes?"
        onConfirm={handleConfirmDiscard}
        onCancel={() => setShowConfirm(false)}
        data-testid="confirm-discard"
      />

      {isSaving && (
        <MapOverlay>
          <Paper sx={{ py: 1, px: 3, textAlign: "center", width: 200 }}>
            <BlockSpinner spinnerProps={{ size: 20 }} />
            <Typography component="p" variant="caption" pt={1}>
              Saving changes...
            </Typography>
          </Paper>
        </MapOverlay>
      )}
    </>
  );
};

export default MuPowerLeadInDraw;
